import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { URLS } from '../../../shared/constants/api-request.urls';
import {
  FinancialStatementDifferencePayload,
  FinancialStatementDifferenceResponse,
  FinancialStatementOption,
} from '../models/financial-statement.model';

@Injectable({ providedIn: 'root' })
export class FinancialStatementsService {
  private http = inject(HttpClient);

  getFinancialStatementUrl(statementType: string): string {
    switch (statementType) {
      case 'IncomeStatement':
        return URLS.INCOME_STATEMENT;
      case 'CashFlow':
        return URLS.CASH_FLOW;
      case 'BalanceSheet':
      default:
        return URLS.BALANCE_SHEET;
    }
  }

  getFinancialStatements(
    stockId?: string,
    filter?: string,
    statementType: string = 'BalanceSheet',
    pageSize: number = 50,
    fromDate?: string,
    toDate?: string
  ): Observable<any> {
    let params = new HttpParams();
    if (stockId) {
      params = params.set('stockId', stockId);
    }
    if (filter) {
      params = params.set('filter', filter);
    }
    if (pageSize) {
      params = params.set('pageSize', pageSize.toString());
    }
    if (fromDate) {
      params = params.set('fromDate', fromDate);
    }
    if (toDate) {
      params = params.set('toDate', toDate);
    }

    const apiUrl = this.getFinancialStatementUrl(statementType);
    return this.http.get(apiUrl, { params });
  }

  getFinancialStatement(id: string, statementType: string = 'BalanceSheet'): Observable<any> {
    const apiUrl = this.getFinancialStatementUrl(statementType);
    return this.http.get(`${apiUrl}/${id}?mode=edit`);
  }

  createFinancialStatement(
    financialStatement: any,
    statementType: string = 'BalanceSheet'
  ): Observable<any> {
    console.log(`Creating ${statementType}:`, financialStatement);
    const apiUrl = this.getFinancialStatementUrl(statementType);
    return this.http.post(apiUrl, financialStatement);
  }

  updateFinancialStatement(
    id: string,
    financialStatement: any,
    statementType: string = 'BalanceSheet',
    copyFromId?: string
  ): Observable<any> {
    const apiUrl = this.getFinancialStatementUrl(statementType);
    return this.http.put(`${apiUrl}/${id}`, { ...financialStatement, copyFromId });
  }

  deleteFinancialStatement(id: string, statementType: string = 'BalanceSheet'): Observable<any> {
    const apiUrl = this.getFinancialStatementUrl(statementType);
    return this.http.delete(`${apiUrl}/${id}`);
  }

  getFinancialStatementOptions(
    stockId: string,
    statementType: string
  ): Observable<{ data: FinancialStatementOption[] }> {
    const apiUrl = this.getFinancialStatementUrl(statementType);
    return this.http
      .get(`${apiUrl}/financial-statements`, {
        params: { stockId },
      })
      .pipe(map((res: any) => res.data));
  }

  calculateFinancialDifference(
    financialStatementDifferencePayload: FinancialStatementDifferencePayload
  ): Observable<FinancialStatementDifferenceResponse> {
    return this.http
      .post(`${URLS.HOME}/calculate-financial-differences`, financialStatementDifferencePayload)
      .pipe(map((res: any) => res.data));
  }

  createFinancialStatementsByFilter(payload: {
    stockId: string;
    period: number;
    dateTime: Date;
    type: number;
    year: number;
  }): Observable<{ createdCount: number; createdFinancialStatementIds: string[] }> {
    return this.http
      .post(`${URLS.DRAFTED_FINANCIAL_STATEMENT}/financial-statements`, payload)
      .pipe(map((res: any) => res.data));
  }

  deleteFinancialStatementsByFilter(payload: {
    stockId: string;
    period: number;
    dateTime: Date;
  }): Observable<any> {
    return this.http.delete<any>(`${URLS.DRAFTED_FINANCIAL_STATEMENT}/financial-statements`, {
      body: payload,
    });
  }
}
